import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import SEO from '@/components/common/SEO'

const M = '#06060c'
const D = '#0a0a14'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

const getPresetAmounts = (t: any) => [
  { cents: 2900, label: '$29', desc: t('university.give.presets.m1', 'Funds 1 Month') },
  { cents: 9900, label: '$99', desc: t('university.give.presets.m6', '6 Month Scholarship') },
  { cents: 17900, label: '$179', desc: t('university.give.presets.m12', 'Full Year Scholarship') },
  { cents: 50000, label: '$500', desc: t('university.give.presets.partner', 'Impact Partner') },
]

const getCauses = (t: any) => [
  { value: 'general', label: t('university.gift.causes.general', '💙 General scholarship fund'), desc: t('university.give.causes.desc_general', 'Goes wherever need is greatest') },
  { value: 'domestic_abuse', label: t('university.gift.causes.domestic_abuse', '🏠 Domestic abuse survivors'), desc: t('university.give.causes.desc_abuse', 'Women rebuilding their lives') },
  { value: 'refugee', label: t('university.gift.causes.refugee', '🌍 Refugees & asylum seekers'), desc: t('university.give.causes.desc_refugee', 'New country, new start') },
  { value: 'youth', label: t('university.gift.causes.youth', '🌱 At-risk youth'), desc: t('university.give.causes.desc_youth', 'Young people without opportunity') },
  { value: 'prison', label: t('university.gift.causes.prison', '🔓 Prison rehabilitation'), desc: t('university.give.causes.desc_prison', 'Education changes reoffending rates') },
  { value: 'homelessness', label: t('university.gift.causes.homelessness', '🤝 Homelessness recovery'), desc: t('university.give.causes.desc_homeless', 'Employment through education') },
]

const getStories = (t: any) => [
  { quote: t('university.give.stories.s1_quote', "When I left my marriage with nothing, I didn't know what I'd do. Still Waters gave me a pathway."), cause: 'domestic_abuse', name: t('university.give.stories.s1_name', 'Sarah') },
  { quote: t('university.give.stories.s2_quote', "I arrived speaking no English. The AI teaching in my language changed everything."), cause: 'refugee', name: t('university.give.stories.s2_name', 'Amara') },
  { quote: t('university.give.stories.s3_quote', "I never thought I'd study at university level. Your generosity made it possible."), cause: 'general', name: t('university.give.stories.s3_name', 'Michael') },
]

export default function GivePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()

  const PRESET_AMOUNTS = getPresetAmounts(t)
  const CAUSES = getCauses(t)
  const STORIES = getStories(t)

  const [isMobile, setIsMobile] = useState(false)
  const [selectedAmount, setSelectedAmount] = useState(2900)
  const [customAmount, setCustomAmount] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [selectedCause, setSelectedCause] = useState('general')
  const [donorEmail, setDonorEmail] = useState(user?.email || '')
  const [donorName, setDonorName] = useState('')
  const [dedication, setDedication] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [fundBalance, setFundBalance] = useState<{ balance_cents: number; total_donors: number; total_recipients: number } | null>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check(); window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => { if (user?.email) setDonorEmail(user.email) }, [user])

  // Load fund balance
  useEffect(() => {
    async function loadBalance() {
      try {
        const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl
        const res = await fetch(`${supabaseUrl}/functions/v1/scholarship-fund`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'fund_balance' }),
        })
        if (res.ok) { const d = await res.json(); setFundBalance(d) }
      } catch { /* non-fatal */ }
    }
    loadBalance()
  }, [])

  const finalAmount = isCustom
    ? Math.round(parseFloat(customAmount || '0') * 100)
    : selectedAmount

  async function handleDonate() {
    if (!donorEmail.trim()) { setError(t('university.give.errorEmail', 'Please enter your email address.')); return }
    if (finalAmount < 500) { setError(t('university.give.errorAmount', 'Minimum donation is $5.')); return }

    setLoading(true); setError(null)

    try {
      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl

      const res = await fetch(`${supabaseUrl}/functions/v1/scholarship-fund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'donate',
          amount_cents: finalAmount,
          donor_email: donorEmail.trim(),
          donor_name: anonymous ? undefined : (donorName.trim() || undefined),
          donor_anonymous: anonymous,
          dedication: dedication.trim() || undefined,
          cause_tag: selectedCause,
          success_url: `${window.location.origin}/give/thankyou`,
          cancel_url: `${window.location.origin}/give`,
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Donation failed')
      if (data.url) window.location.href = data.url
    } catch (err) {
      setError((err as Error).message)
      setLoading(false)
    }
  }

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        title="Pay It Forward: Fund a Scholarship"
        description="Join our mission to democratize education. Help refugees, domestic abuse survivors, and at-risk youth access university-level learning through our scholarship fund."
      />

      {/* Hero */}
      <div style={{ background: `linear-gradient(180deg, #0a0a1a 0%, ${D} 100%)`, borderBottom: `1px solid ${BR}` }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '40px 20px 36px' : '60px 24px 48px', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>💙</div>
            <p style={{ fontSize: 11, color: IN, fontWeight: 800, letterSpacing: '2.5px', marginBottom: 10 }}>
              {t('university.give.eyebrow', 'PAY IT FORWARD')}
            </p>
            <h1 style={{ fontSize: isMobile ? 28 : 42, fontWeight: 900, margin: '0 0 16px', letterSpacing: '-1px', fontFamily: "'Playfair Display', Georgia, serif" }}>
              {t('university.give.title', 'Fund an Education for Someone in Need')}
            </h1>
            <p style={{ color: MU, fontSize: isMobile ? 14 : 16, lineHeight: 1.75, maxWidth: 560, margin: '0 auto 28px' }}>
              {t('university.give.subtitle', 'A woman escaping abuse. A refugee starting over. A young person with no opportunity. Your contribution gives them what you take for granted — the ability to learn.')}
            </p>

            {/* Live fund stats */}
            {fundBalance && (
              <div style={{ display: 'inline-flex', gap: 28, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BR}`, borderRadius: 100, padding: '10px 24px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <span style={{ fontSize: 13, color: MU }}>
                  <strong style={{ color: G }}>{fundBalance.total_donors}</strong> {t('university.give.statDonors', 'donors')}
                </span>
                <span style={{ fontSize: 13, color: MU }}>
                  <strong style={{ color: '#4ade80' }}>{fundBalance.total_recipients}</strong> {t('university.give.statRecipients', 'students funded')}
                </span>
                <span style={{ fontSize: 13, color: MU }}>
                  <strong style={{ color: TX }}>${(fundBalance.balance_cents / 100).toFixed(0)}</strong> {t('university.give.statBalance', 'available')}
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '48px 24px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 32, alignItems: 'start' }}>

          {/* Left — donation form */}
          <div>
            {/* Amount */}
            <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 18, padding: '24px', marginBottom: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 18 }}>
                {t('university.give.amountTitle', 'HOW MUCH WOULD YOU LIKE TO GIVE?')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 12 }}>
                {PRESET_AMOUNTS.map(a => (
                  <motion.button key={a.cents} whileHover={{ scale: 1.02 }}
                    onClick={() => { setSelectedAmount(a.cents); setIsCustom(false) }}
                    style={{ background: !isCustom && selectedAmount === a.cents ? 'rgba(212,168,67,0.1)' : CA, border: `${!isCustom && selectedAmount === a.cents ? 2 : 1}px solid ${!isCustom && selectedAmount === a.cents ? G : BR}`, borderRadius: 12, padding: '14px 12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: !isCustom && selectedAmount === a.cents ? G : TX, marginBottom: 3 }}>{a.label}</div>
                    <div style={{ fontSize: 11, color: MU, lineHeight: 1.4 }}>{a.desc}</div>
                  </motion.button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setIsCustom(true)}
                  style={{ background: isCustom ? 'rgba(212,168,67,0.08)' : 'rgba(255,255,255,0.04)', border: `${isCustom ? 2 : 1}px solid ${isCustom ? G : BR}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: isCustom ? G : MU, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {t('university.give.custom', 'Custom')}
                </button>
                {isCustom && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                    <span style={{ color: MU, fontSize: 18, fontWeight: 700 }}>$</span>
                    <input value={customAmount} onChange={e => setCustomAmount(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" type="number" min="5"
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `1.5px solid rgba(212,168,67,0.4)`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 18, fontWeight: 700, outline: 'none', fontFamily: "'Inter', sans-serif" }} />
                  </div>
                )}
              </div>
            </div>

            {/* Cause */}
            <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 18, padding: '24px', marginBottom: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 18 }}>
                {t('university.give.causeTitle', 'DIRECT YOUR CONTRIBUTION')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CAUSES.map(c => (
                  <motion.button key={c.value} whileHover={{ x: 2 }} onClick={() => setSelectedCause(c.value)}
                    style={{ background: selectedCause === c.value ? 'rgba(14,165,233,0.1)' : 'transparent', border: `1px solid ${selectedCause === c.value ? 'rgba(14,165,233,0.35)' : BR}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selectedCause === c.value ? IN : BR}`, background: selectedCause === c.value ? IN : 'transparent', flexShrink: 0, transition: 'all 0.15s' }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: selectedCause === c.value ? TX : 'rgba(255,255,255,0.7)', marginBottom: 2 }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: MU }}>{c.desc}</div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Dedication & details */}
            <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 18, padding: '24px', marginBottom: 24 }}>
              <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 18 }}>
                {t('university.give.detailsTitle', 'YOUR DETAILS')}
              </p>

              {[
                { label: t('university.give.emailLabel', 'Your Email *'), value: donorEmail, set: setDonorEmail, placeholder: 'you@email.com', type: 'email' },
                { label: t('university.give.nameLabel', 'Your Name (optional)'), value: donorName, set: setDonorName, placeholder: 'Your name', type: 'text' },
              ].map(f => (
                <div key={f.label} style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>{f.label}</label>
                  <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} type={f.type}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>
                  {t('university.give.dedicationLabel', 'Dedication (optional)')}
                </label>
                <input value={dedication} onChange={e => setDedication(e.target.value)} placeholder={t('university.give.dedicationPlaceholder', 'e.g. In memory of my mother, who never got the education she deserved')}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
              </div>

              <button onClick={() => setAnonymous(a => !a)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${anonymous ? G : BR}`, background: anonymous ? G : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
                  {anonymous && <span style={{ fontSize: 11, color: '#06060c', fontWeight: 900 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, color: MU }}>{t('university.give.anonymous', 'Make this donation anonymous')}</span>
              </button>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#f87171' }}>
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={handleDonate} disabled={loading || finalAmount < 500}
              style={{ width: '100%', background: finalAmount >= 500 ? `linear-gradient(135deg, #0ea5e9, #0284c7)` : 'rgba(255,255,255,0.06)', color: finalAmount >= 500 ? '#fff' : MU, border: 'none', borderRadius: 14, padding: '18px', fontSize: 16, fontWeight: 800, cursor: finalAmount >= 500 ? 'pointer' : 'not-allowed', opacity: loading ? 0.7 : 1, boxShadow: finalAmount >= 500 ? '0 6px 24px rgba(14,165,233,0.35)' : 'none' }}>
              {loading ? t('university.give.processing', 'Processing…') : `💙 ${t('university.give.donateBtn', 'Give')} $${(finalAmount / 100).toFixed(0)} ${t('university.give.donateBtnSuffix', '→ Fund an Education')}`}
            </motion.button>
            <p style={{ fontSize: 12, color: MU, textAlign: 'center', marginTop: 12 }}>
              🔒 {t('university.give.secure', 'Secure via Stripe · You receive a receipt · 100% goes to scholarship fund')}
            </p>
          </div>

          {/* Right — impact + stories */}
          <div>
            {/* Impact stats */}
            <div style={{ background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 18, padding: '22px', marginBottom: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 800, color: '#7dd3fc', letterSpacing: '1.5px', marginBottom: 16 }}>
                {t('university.give.impactTitle', 'WHAT YOUR GIFT DOES')}
              </p>
              {[
                { amount: '$29', impact: t('university.give.impact1', 'Funds a full month of unlimited learning for one person') },
                { amount: '$99', impact: t('university.give.impact2', 'Funds six months of university-level education') },
                { amount: '$179', impact: t('university.give.impact3', 'Funds an entire year of unlimited learning') },
                { amount: '$500', impact: t('university.give.impact4', 'Impact Partner — funds multiple students this year') },
              ].map(i => (
                <div key={i.amount} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                  <span style={{ background: 'rgba(14,165,233,0.15)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 800, color: '#7dd3fc', flexShrink: 0 }}>{i.amount}</span>
                  <span style={{ fontSize: 13, color: MU, lineHeight: 1.55 }}>{i.impact}</span>
                </div>
              ))}
            </div>

            {/* Stories */}
            {STORIES.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '20px', marginBottom: 14 }}>
                <p style={{ fontSize: 14, color: TX, lineHeight: 1.65, fontStyle: 'italic', margin: '0 0 12px' }}>"{s.quote}"</p>
                <p style={{ fontSize: 12, color: MU, margin: 0, fontWeight: 600 }}>— {s.name}</p>
              </motion.div>
            ))}

            {/* Gift alternative nudge */}
            <div style={{ background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.2)', borderRadius: 16, padding: '18px 20px' }}>
              <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                {t('university.give.giftNudge', 'Have someone specific in mind?')}
              </p>
              <p style={{ fontSize: 12, color: MU, margin: '0 0 12px', lineHeight: 1.5 }}>
                {t('university.give.giftNudgeText', 'Gift a degree directly to a friend, family member, or someone you know who needs it.')}
              </p>
              <button onClick={() => navigate('/gift')} style={{ background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.25)', borderRadius: 10, padding: '9px 16px', color: G, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {t('university.give.giftBtn', 'Gift a Degree →')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
